import { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { api } from './lib/api'
import { AuthProvider, useAuth } from './lib/auth'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import Login from './pages/Login'
const Overview = lazy(() => import('./pages/Overview'))
const CommissionManagement = lazy(() => import('./pages/CommissionManagement'))
const ServiceCharges = lazy(() => import('./pages/ServiceCharges'))
const PractitionerEarnings = lazy(() => import('./pages/PractitionerEarnings'))
const Configuration = lazy(() => import('./pages/Configuration'))
const AddPractitioner = lazy(() => import('./pages/AddPractitioner'))
const ChargesControl = lazy(() => import('./pages/ChargesControl'))
const MyPassword = lazy(() => import('./pages/MyPassword'))
const Offers = lazy(() => import('./pages/Offers'))
const Gaps = lazy(() => import('./pages/Gaps'))
const Waitlist = lazy(() => import('./pages/Waitlist'))
const AtRisk = lazy(() => import('./pages/AtRisk'))
const UpliftDownlift = lazy(() => import('./pages/UpliftDownlift'))
const Retention = lazy(() => import('./pages/Retention'))
const HourlyPay = lazy(() => import('./pages/HourlyPay'))
const RetentionCalculation = lazy(() => import('./pages/RetentionCalculation'))
const PlanFeeRates = lazy(() => import('./pages/PlanFeeRates'))
const PracticePlans = lazy(() => import('./pages/PracticePlans'))
const Summary = lazy(() => import('./pages/Summary'))
const InvoiceCards = lazy(() => import('./pages/InvoiceCards'))
const Bills = lazy(() => import('./pages/Bills'))
const Refunds = lazy(() => import('./pages/Refunds'))
const Debtors = lazy(() => import('./pages/Debtors'))
const Appointments = lazy(() => import('./pages/Appointments'))
const RotaSetup = lazy(() => import('./pages/RotaSetup'))
const RotaDay = lazy(() => import('./pages/RotaDay'))
const RotaWeek = lazy(() => import('./pages/RotaWeek'))
const ActivityLog = lazy(() => import('./pages/ActivityLog'))
const MyInvoice = lazy(() => import('./pages/MyInvoice'))
const MyProfile = lazy(() => import('./pages/MyProfile'))
const UploadReport = lazy(() => import('./pages/UploadReport'))
const EmailSettings = lazy(() => import('./pages/EmailSettings'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const BookPractitioner = lazy(() => import('./pages/BookPractitioner'))
const BookingRequests = lazy(() => import('./pages/BookingRequests'))
const BookingSetup = lazy(() => import('./pages/BookingSetup'))
const OpenSpace = lazy(() => import('./pages/OpenSpace'))
const Marketing = lazy(() => import('./pages/Marketing'))
const MarketingAds = lazy(() => import('./pages/MarketingAds'))
const MarketingFunnel = lazy(() => import('./pages/MarketingFunnel'))
const MarketingOrigins = lazy(() => import('./pages/MarketingOrigins'))
const Forms = lazy(() => import('./pages/Forms'))
const Enquiries = lazy(() => import('./pages/Enquiries'))
const PublicForm = lazy(() => import('./pages/PublicForm'))
const MarketingPageDesign = lazy(() => import('./pages/MarketingPageDesign'))
const Discovery = lazy(() => import('./pages/Discovery'))
const Team = lazy(() => import('./pages/Team'))
const Apps = lazy(() => import('./pages/Apps'))
const TeamMember = lazy(() => import('./pages/TeamMember'))
const TeamPerson = lazy(() => import('./pages/TeamPerson'))
const MarketingAdPatients = lazy(() => import('./pages/MarketingAdPatients'))
const Recordings = lazy(() => import('./pages/Recordings'))
const RecordingPlay = lazy(() => import('./pages/RecordingPlay'))
const RecordingSetup = lazy(() => import('./pages/RecordingSetup'))
const PlatformNewClient = lazy(() => import('./pages/PlatformNewClient'))
const PlatformClient = lazy(() => import('./pages/PlatformClient'))
const PlatformPackages = lazy(() => import('./pages/PlatformPackages'))
const GettingStarted = lazy(() => import('./pages/GettingStarted'))
const MarketingSegments = lazy(() => import('./pages/MarketingSegments'))
const ResearchSundries = lazy(() => import('./pages/ResearchSundries'))
const MarketingSegmentEdit = lazy(() => import('./pages/MarketingSegmentEdit'))
const MarketingCampaigns = lazy(() => import('./pages/MarketingCampaigns'))
const MarketingCampaignEdit = lazy(() => import('./pages/MarketingCampaignEdit'))
const PlatformAccounts = lazy(() => import('./pages/PlatformAccounts'))
const PlatformBilling = lazy(() => import('./pages/PlatformBilling'))
const PublicBooking = lazy(() => import('./pages/PublicBooking'))
const SharedPayslip = lazy(() => import('./pages/SharedPayslip'))
const PreVisitForm = lazy(() => import('./pages/PreVisitForm'))
const PreVisitStart = lazy(() => import('./pages/PreVisitForm').then((m) => ({ default: m.PreVisitStart })))
const Portal = lazy(() => import('./pages/Portal'))
const Inventory = lazy(() => import('./pages/Inventory'))
const InventoryOverview = lazy(() => import('./pages/InventoryOverview'))
const InventorySales = lazy(() => import('./pages/InventorySales'))
const InventoryPatient = lazy(() => import('./pages/InventoryPatient'))
const Patient = lazy(() => import('./pages/Patient'))
const InventoryStocktake = lazy(() => import('./pages/InventoryStocktake'))
const InventoryMoves = lazy(() => import('./pages/InventoryMoves'))
const Automations = lazy(() => import('./pages/Automations'))
const AutomationEdit = lazy(() => import('./pages/AutomationEdit'))
const AutomationInbox = lazy(() => import('./pages/AutomationInbox'))
const AutomationHistory = lazy(() => import('./pages/AutomationHistory'))
const Chat = lazy(() => import('./pages/Chat'))
const WhatsAppSetup = lazy(() => import('./pages/WhatsAppSetup'))
const WhatsAppReminders = lazy(() => import('./pages/WhatsAppReminders'))
const ChatAnswers = lazy(() => import('./pages/ChatAnswers'))
const ChatTreatments = lazy(() => import('./pages/ChatTreatments'))
const ChatFlows = lazy(() => import('./pages/ChatFlows'))
const PreVisitForms = lazy(() => import('./pages/PreVisitForms'))
const PaymentMap = lazy(() => import('./pages/PaymentMap'))
const MyMonth = lazy(() => import('./pages/MyMonth'))
import { Spinner } from './components/ui'

/** `/` is the admin dashboard; a practitioner belongs on their own invoices. */
function IndexRoute() {
  const { user } = useAuth()
  if (user?.role === 'practitioner') return <Navigate to="/my-month" replace />
  // A platform admin runs Lynqpod, not a practice. The finance overview would query
  // a practice's data they are not a member of, so send them to their own console.
  if (user?.role === 'platform_admin') return <Navigate to="/platform/accounts" replace />
  return <Overview />
}

/**
 * app.lynqpod.com is the Lynqpod console now; a practice's team works on the practice's
 * own address. An old bookmark to a practice page on the console address goes on to the
 * same page there. The console's own pages and its sign-in stay put.
 */
function useConsoleForwarding() {
  const [checked, setChecked] = useState(false)
  useEffect(() => {
    const p = window.location.pathname
    if (p === '/login' || p.startsWith('/platform') || p.startsWith('/book/') || p.startsWith('/p/') || p.startsWith('/f/') || p.startsWith('/form/') || p.startsWith('/portal')) { setChecked(true); return }
    api.get<{ console: boolean; practiceUrl: string }>('/where')
      .then((r) => {
        if (r.data.console && r.data.practiceUrl && p !== '/') { window.location.replace(r.data.practiceUrl + p + window.location.search); return }
        setChecked(true)
      })
      .catch(() => setChecked(true))
  }, [])
  return checked
}

// Lets Sentry name page loads by route ("/booking/:practitionerId") rather than the address.
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

function Shell() {
  const { user, loading } = useAuth()
  const forwarded = useConsoleForwarding()
  if (loading || !forwarded) return <Spinner />
  return (
    /*
     * EVERY PAGE ARRIVES WHEN IT IS ASKED FOR.
     *
     * All 83 of them used to be in the one file the browser downloads before it can show
     * anything: 406 KB gzipped to open a sign-in box. Now each page is its own small file,
     * fetched the first time somebody opens it and cached from then on.
     *
     * The fallback is deliberately empty rather than a spinner. These files come from the
     * same server the page is already talking to and arrive in tens of milliseconds; a
     * spinner that flashes for 40ms reads as a fault, not as progress. The sidebar and the
     * header stay put throughout, because they are outside this.
     */
    <Suspense fallback={<div className="min-h-[40vh]" aria-busy="true" />}>
    <SentryRoutes>
      {/* Once signed in, /login must not keep rendering the form — that is what
          made sign-in look like it had failed when the token was already stored. */}
      <Route path="/login" element={
        user ? <Navigate to={user.role === 'practitioner' ? '/my-month' : '/'} replace /> : <Login />} />
      {/* The only unauthenticated page in the application. Declared before the
          protected tree so a signed-out patient reaches it instead of /login, and
          outside Layout so they never see the staff navigation. */}
      <Route path="/book/:slug" element={<PublicBooking />} />
      {/* The practice-wide page: what you need first, then any clinician who offers it. */}
      <Route path="/book" element={<PublicBooking />} />
      {/* One payslip, opened with a link rather than a login. Outside ProtectedRoute
          on purpose: the token in the URL is the credential, and it is scoped to a
          single practitioner and month, expires, and can be revoked. */}
      <Route path="/p/:token" element={<SharedPayslip />} />
      {/* The patient's pre-visit form. No login: the token in the path is the credential. */}
      {/* Before /f/:token, which would otherwise treat "start" as a token. */}
      <Route path="/f/start" element={<PreVisitStart />} />
      <Route path="/f/:token" element={<PreVisitForm />} />
      {/* A practice's own enquiry form. No login: the address is the whole credential,
          and the page asks only what the practice put on it. */}
      <Route path="/form/:slug" element={<PublicForm />} />
      {/* The patient portal. No staff auth: the session is the patient's own token. */}
      <Route path="/portal" element={<Portal />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<IndexRoute />} />
        <Route path="commission-management" element={
          <ProtectedRoute roles={['admin', 'data_admin']}><CommissionManagement /></ProtectedRoute>} />
        <Route path="add-practitioner" element={
          <ProtectedRoute roles={['data_admin']}><AddPractitioner /></ProtectedRoute>} />
        <Route path="config" element={
          <ProtectedRoute roles={['admin']}><Configuration /></ProtectedRoute>} />
        <Route path="service-charges" element={
          <ProtectedRoute roles={['admin']}><ServiceCharges /></ProtectedRoute>} />
        <Route path="practitioner-earnings" element={
          <ProtectedRoute roles={['admin']}><PractitionerEarnings /></ProtectedRoute>} />
        {(['charges','uplift-downlift','retention-management','retention-calculation','plan-fee-rates','practice-plans','hourly'] as const).map((path) => {
          const El = { 'charges': ChargesControl, 'uplift-downlift': UpliftDownlift,
                       'retention-management': Retention, 'retention-calculation': RetentionCalculation,
                       'plan-fee-rates': PlanFeeRates, 'practice-plans': PracticePlans,
                       'hourly': HourlyPay }[path]
          return <Route key={path} path={path} element={
            <ProtectedRoute roles={['admin','data_admin']}><El /></ProtectedRoute>} />
        })}
        <Route path="payment-map" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><PaymentMap /></ProtectedRoute>} />
        <Route path="pre-visit" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><PreVisitForms /></ProtectedRoute>} />
        {/* The Patients module went: change requests, orders and what we offer, three pages
            whose collections were empty on every practice that has ever run. The /offerings
            API stays, because the patient portal reads it (pages/Portal.tsx). */}
        {/* The shelf. Same guard as the orders it is spent on. */}
        <Route path="inventory" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><InventoryOverview /></ProtectedRoute>} />
        <Route path="inventory/stock" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><Inventory /></ProtectedRoute>} />
        <Route path="inventory/sold" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><InventorySales /></ProtectedRoute>} />
        <Route path="inventory/stocktake" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><InventoryStocktake /></ProtectedRoute>} />
        {/* One patient's file. Where search sends you when you click a name. */}
        <Route path="patients/:patientId" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><Patient /></ProtectedRoute>} />
        <Route path="inventory/patient/:patientId" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><InventoryPatient /></ProtectedRoute>} />
        <Route path="inventory/moves" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><InventoryMoves /></ProtectedRoute>} />
        {/* The practice's own rules. inbox and history before :id, or they would be read as ids. */}
        <Route path="automations" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><Automations /></ProtectedRoute>} />
        <Route path="automations/inbox" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><AutomationInbox /></ProtectedRoute>} />
        <Route path="automations/history" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><AutomationHistory /></ProtectedRoute>} />
        <Route path="automations/:id" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><AutomationEdit /></ProtectedRoute>} />
        <Route path="chat" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><Chat /></ProtectedRoute>} />
        {/* WhatsApp: the Chat inbox with only its WhatsApp conversations, and the number's setup. */}
        {/* Chair time: slots that came free, the waiting list, and appointments at risk. */}
        <Route path="gaps" element={<ProtectedRoute roles={['data_admin', 'admin']}><Gaps /></ProtectedRoute>} />
        <Route path="gaps/waitlist" element={<ProtectedRoute roles={['data_admin', 'admin']}><Waitlist /></ProtectedRoute>} />
        <Route path="gaps/at-risk" element={<ProtectedRoute roles={['data_admin', 'admin']}><AtRisk /></ProtectedRoute>} />
        <Route path="whatsapp" element={<ProtectedRoute roles={['data_admin', 'admin']}><Chat channel="whatsapp" /></ProtectedRoute>} />
        <Route path="whatsapp/setup" element={<ProtectedRoute roles={['data_admin', 'admin']}><WhatsAppSetup /></ProtectedRoute>} />
        <Route path="whatsapp/reminders" element={<ProtectedRoute roles={['data_admin', 'admin']}><WhatsAppReminders /></ProtectedRoute>} />
        <Route path="chat/answers" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><ChatAnswers /></ProtectedRoute>} />
        <Route path="chat/treatments" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><ChatTreatments /></ProtectedRoute>} />
        <Route path="chat/flows" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><ChatFlows /></ProtectedRoute>} />
        <Route path="chat/flows/:id" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><ChatFlows /></ProtectedRoute>} />
        <Route path="chat/:id" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><Chat /></ProtectedRoute>} />
        <Route path="summary" element={
          <ProtectedRoute roles={['data_admin']}><Summary /></ProtectedRoute>} />
        <Route path="activity" element={<Navigate to="/settings/activity" replace />} />
        <Route path="appointments" element={
          <ProtectedRoute roles={['admin','data_admin']}><Appointments /></ProtectedRoute>} />
        <Route path="rota" element={
          <ProtectedRoute roles={['admin','data_admin']}><RotaDay /></ProtectedRoute>} />
        <Route path="rota/week" element={
          <ProtectedRoute roles={['admin','data_admin']}><RotaWeek /></ProtectedRoute>} />
        <Route path="rota/setup" element={
          <ProtectedRoute roles={['admin','data_admin']}><RotaSetup /></ProtectedRoute>} />
        {/* The rota was briefly four pages. Old links still land somewhere sensible. */}
        <Route path="rota/day" element={<Navigate to="/rota" replace />} />
        <Route path="rota/nurses" element={<Navigate to="/rota" replace />} />
        <Route path="debtors" element={
          <ProtectedRoute roles={['admin','data_admin']}><Debtors /></ProtectedRoute>} />
        <Route path="refunds" element={
          <ProtectedRoute roles={['admin','data_admin']}><Refunds /></ProtectedRoute>} />
        <Route path="bills" element={
          <ProtectedRoute roles={['admin','data_admin']}><Bills /></ProtectedRoute>} />
        <Route path="payslips" element={
          <ProtectedRoute roles={['data_admin']}><InvoiceCards /></ProtectedRoute>} />
        {/* Renamed to Payslips on 2026-09-05. The old path still resolves so a
            bookmark or a pasted link does not 404 — and so Layout can still match
            the nav item, which it does on pathname. */}
        <Route path="cards" element={<Navigate to="/payslips" replace />} />
        {/* Renamed from "invoice" to "payslip" on 2026-09-07: the admin side was
            renamed in September and the practitioner's own pages were missed, so the
            person being paid was the last one still being shown the old word. The old
            path still resolves, so a bookmark or an emailed link does not 404. */}
        <Route path="my-invoice" element={<KeepQuery to="/my-payslips" />} />
        <Route path="my-payslips" element={
          <ProtectedRoute roles={['practitioner']}><MyInvoice /></ProtectedRoute>} />
        <Route path="my-month" element={
          <ProtectedRoute roles={['practitioner']}><MyMonth /></ProtectedRoute>} />
        <Route path="my-profile" element={
          <ProtectedRoute roles={['practitioner']}><MyProfile /></ProtectedRoute>} />
        <Route path="upload-report" element={
          <ProtectedRoute roles={['data_admin']}><UploadReport /></ProtectedRoute>} />
        <Route path="admin-settings" element={
          <ProtectedRoute roles={['admin']}><EmailSettings /></ProtectedRoute>} />
        {/*
          * Resetting SOMEBODY ELSE'S password is a practice-wide job, not a Finance one. It
          * sat under Finance, Settings, where reception and clinicians cannot go and where
          * nobody thinks to look for it. Its old address still works so a bookmark does not
          * break (2026-09-24).
          */}
        <Route path="reset-password" element={<Navigate to="/settings/reset-password" replace />} />
        {/* Booking. Setup is the narrower door (admins), and is declared before the
            :practitionerId route so "setup" is not read as a practitioner id. */}
        <Route path="platform/accounts" element={
          <ProtectedRoute roles={['platform_admin']}><PlatformAccounts /></ProtectedRoute>} />
        <Route path="platform/billing" element={
          <ProtectedRoute roles={['platform_admin']}><PlatformBilling /></ProtectedRoute>} />
        <Route path="marketing" element={
          <ProtectedRoute roles={['admin','data_admin']}><Marketing /></ProtectedRoute>} />
        <Route path="marketing/ads" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><MarketingAds /></ProtectedRoute>} />
        <Route path="marketing/offers" element={
          <ProtectedRoute roles={['admin','data_admin']}><Offers /></ProtectedRoute>} />
        <Route path="marketing/funnel" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><MarketingFunnel /></ProtectedRoute>} />
        <Route path="marketing/origins" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><MarketingOrigins /></ProtectedRoute>} />
        <Route path="forms" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><Forms /></ProtectedRoute>} />
        <Route path="forms/enquiries" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><Enquiries /></ProtectedRoute>} />
        {/* Team: practice managers and CEOs (the server checks the account, routes/team.routes.js). */}
        <Route path="team" element={<ProtectedRoute roles={['data_admin']}><Team /></ProtectedRoute>} />
        <Route path="team/add" element={<ProtectedRoute roles={['data_admin']}><AddPractitioner /></ProtectedRoute>} />
        <Route path="team/:id" element={<ProtectedRoute roles={['data_admin']}><TeamMember /></ProtectedRoute>} />
        <Route path="team/person/:kind/:id" element={<ProtectedRoute roles={['data_admin']}><TeamPerson /></ProtectedRoute>} />
        {/* Settings, outside the modules (the gear in the sidebar footer). Connected apps is
            for practice managers and CEOs; the server checks the account (routes/apps.routes.js). */}
        {/* Settings is about the person and the practice, not about one module, so it sits
            outside them. Change password is open to everyone signed in; the other two keep
            the roles they already had. */}
        <Route path="settings" element={<Navigate to="/settings/password" replace />} />
        <Route path="settings/password" element={<ProtectedRoute><MyPassword /></ProtectedRoute>} />
        <Route path="settings/reset-password" element={
          <ProtectedRoute roles={['admin','data_admin']}><ResetPassword /></ProtectedRoute>} />
        <Route path="settings/activity" element={
          <ProtectedRoute roles={['admin','data_admin']}><ActivityLog /></ProtectedRoute>} />
        <Route path="settings/apps" element={<ProtectedRoute roles={['data_admin']}><Apps /></ProtectedRoute>} />
        <Route path="apps" element={<Navigate to="/settings/apps" replace />} />
        <Route path="marketing/page-design" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><MarketingPageDesign /></ProtectedRoute>} />
        <Route path="marketing/discovery" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><Discovery /></ProtectedRoute>} />
        <Route path="marketing/ad-patients" element={
          <ProtectedRoute roles={['data_admin', 'admin']}><MarketingAdPatients /></ProtectedRoute>} />
        <Route path="platform/new-client" element={
          <ProtectedRoute roles={['platform_admin']}><PlatformNewClient /></ProtectedRoute>} />
        <Route path="platform/clients/:id" element={
          <ProtectedRoute roles={['platform_admin']}><PlatformClient /></ProtectedRoute>} />
        <Route path="platform/packages" element={
          <ProtectedRoute roles={['platform_admin']}><PlatformPackages /></ProtectedRoute>} />
        <Route path="getting-started" element={
          <ProtectedRoute roles={['admin','data_admin']}><GettingStarted /></ProtectedRoute>} />
        <Route path="recordings" element={
          <ProtectedRoute roles={['admin','data_admin']}><Recordings /></ProtectedRoute>} />
        <Route path="recordings/setup" element={
          <ProtectedRoute roles={['admin','data_admin']}><RecordingSetup /></ProtectedRoute>} />
        <Route path="recordings/:id" element={
          <ProtectedRoute roles={['admin','data_admin']}><RecordingPlay /></ProtectedRoute>} />
        <Route path="research/sundries" element={
          <ProtectedRoute roles={['admin','data_admin']}><ResearchSundries /></ProtectedRoute>} />
        <Route path="marketing/segments" element={
          <ProtectedRoute roles={['admin','data_admin']}><MarketingSegments /></ProtectedRoute>} />
        <Route path="marketing/segments/:id" element={
          <ProtectedRoute roles={['admin','data_admin']}><MarketingSegmentEdit /></ProtectedRoute>} />
        <Route path="marketing/campaigns" element={
          <ProtectedRoute roles={['admin','data_admin']}><MarketingCampaigns /></ProtectedRoute>} />
        <Route path="marketing/campaigns/:id" element={
          <ProtectedRoute roles={['admin','data_admin']}><MarketingCampaignEdit /></ProtectedRoute>} />
        <Route path="booking/setup" element={
          <ProtectedRoute roles={['admin','data_admin']}><BookingSetup /></ProtectedRoute>} />
        <Route path="booking/openings" element={
          <ProtectedRoute roles={['admin','data_admin','practitioner']}><OpenSpace /></ProtectedRoute>} />
        <Route path="booking/requests" element={
          <ProtectedRoute roles={['admin','data_admin','practitioner']}><BookingRequests /></ProtectedRoute>} />
        <Route path="booking" element={
          <ProtectedRoute roles={['admin','data_admin','practitioner']}><BookPractitioner /></ProtectedRoute>} />
        <Route path="booking/:practitionerId" element={
          <ProtectedRoute roles={['admin','data_admin','practitioner']}><BookPractitioner /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </SentryRoutes>
    </Suspense>
  )
}

/**
 * A redirect that keeps the query string.
 *
 * `My profile` links at `?month=2026-08`, and a plain <Navigate> drops it — so the old
 * bookmark would resolve but land on the wrong month, which is worse than a 404 because
 * nothing looks broken.
 */
function KeepQuery({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

export default function App() {
  return (
    <BrowserRouter basename="/">
      <AuthProvider><Shell /></AuthProvider>
    </BrowserRouter>
  )
}
